import {Empty, fromResult, toEither, toPage} from 'Core/models/Async';
import {ApplicationProcessingDetail} from 'Enrollment/models/ESRBPStatusDetails';
import {sequenceS} from 'fp-ts/lib/Apply';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import * as qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsync} from 'Shared/components/RenderAsync';
import {toShortDateTimeWithSeconds} from 'Shared/utilities/DateFormatter';
import {
  getEnrollmentFileApplicationProcessingDetail,
  getEnrollmentFileApplicationDetailById,
  changeEnrollmentFileApplicationProcessingDetail,
  changeEnrollmentFilesApplicationDetail,
} from '../../ducks/enrollmentFileProcessing';
import './EnrollmentFileDetails.sass';
import CardField from 'Shared/components/CardField';
import {EnrollmentFileApplicationDetail} from 'Enrollment/models/EnrollmentFileApplicationDetails';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {FinalDispositionStatus} from 'Shared/constants/FinalDispositionStatus';
import {EnrollmentApplicationStatus} from 'Shared/constants/EnrollmentApplicationStatus';

type ESRBPStatusDetailsProps = {
  applicationDetails: ApplicationProcessingDetail[];
  enrollmentApplications: EnrollmentFileApplicationDetail;
};

const ESRBPStatusDetails = () => {
  const {applicationProcessingDetails, enrollmentFileApplicationDetail} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const history = useHistory();
  const dispatch = useDispatch();
  const {search} = useLocation();
  const containsSpecialCharacter = /[^A-Za-z0-9]+/;
  const {caseRequestId, id} = qs.parse(search) as {caseRequestId: string; id: string};
  const finalDispositionStatus = (qs.parse(search).finalDispositionStatus as unknown) as FinalDispositionStatus;
  const statusId = (qs.parse(search).statusId as unknown) as EnrollmentApplicationStatus;
  useBreadcrumbs(
    'BP Status Details',
    EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_BP_STATUS_DETAILS(
      id!,
      containsSpecialCharacter.test(caseRequestId) ? 'invalidCaseRequestId' : caseRequestId!,
      finalDispositionStatus,
      statusId
    )
  );
  useEffect(() => {
    if (!caseRequestId) {
      history.push(EnrollmentRouteConstants.ENROLLMENT_FILE_PROCESSING);
    }
    if (!id) {
      history.push(
        EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
          containsSpecialCharacter.test(caseRequestId) ? 'invalidCaseRequestId' : caseRequestId,
          finalDispositionStatus,
          statusId
        )
      );
    }
  }, [caseRequestId, id, finalDispositionStatus, history, statusId]);

  useEffect(() => {
    if (id) {
      dispatch(getEnrollmentFileApplicationProcessingDetail(id));
    }
    if (caseRequestId && id) {
      dispatch(getEnrollmentFileApplicationDetailById(id, caseRequestId));
    }
    return () => {
      dispatch(changeEnrollmentFileApplicationProcessingDetail(new Empty()));
      dispatch(changeEnrollmentFilesApplicationDetail(new Empty()));
    };
  }, [id, caseRequestId]);

  const ApplicationsPage = ({applicationDetails, enrollmentApplications}: ESRBPStatusDetailsProps) => {
    return (
      <div className="enrollment-file-details__file-proccessing-tasks">
        <h2 id="enrollment-file-details-bp-status-detail-title" className="enrollment-file-details-header">
          BP Status Details
        </h2>
        <div className="enrollment-file-details__file-details">
          <CardField
            className="enrollment-file-details__details-field"
            name="Request Type"
            value={enrollmentApplications.requestType}
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="SSN"
            value={enrollmentApplications.maskedIdentifier}
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="First Name"
            value={enrollmentApplications.firstName}
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="Last Name"
            value={enrollmentApplications.lastName}
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="Product"
            value={enrollmentApplications.product}
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="Pending Task"
            value={
              applicationDetails.length > 0 && applicationDetails[0].pendingTask! > 0
                ? applicationDetails[0].pendingTask + `${applicationDetails[0].totalTask}`
                : `0/${applicationDetails[0].totalTask}`
            }
          />
          <CardField
            className="enrollment-file-details__details-field"
            name="Completed Task"
            value={
              applicationDetails.length > 0 && applicationDetails[0].completedTask! > 0
                ? applicationDetails[0].completedTask + `/${applicationDetails[0].totalTask}`
                : +`0/${applicationDetails[0].totalTask}`
            }
          />
        </div>
        <PagedView pagedViewKey="esrbpStatusDetails" data={fromResult(toPage(applicationDetails))} loadMore={() => {}}>
          {columnProps => (
            <>
              <Column {...columnProps} title="Task Name" default>
                {processingResult => processingResult.taskName}
              </Column>
              <Column {...columnProps} title="Created Date" default>
                {processingResult => toShortDateTimeWithSeconds(processingResult.createDate!)}
              </Column>
              <Column {...columnProps} title="Updated Date" default>
                {processingResult =>
                  processingResult.updatedDate ? toShortDateTimeWithSeconds(processingResult.updatedDate) : '-'
                }
              </Column>
              <Column {...columnProps} title="Status" default>
                {processingResult => processingResult.status}
              </Column>
              <Column {...columnProps} title="Additional Details" default>
                {processingResult => processingResult.additionalDetails}
              </Column>
            </>
          )}
        </PagedView>
      </div>
    );
  };

  return pipe(
    sequenceS(E.either)({
      applicationProcessingDetails: toEither(applicationProcessingDetails),
      enrollmentFileApplicationDetail: toEither(enrollmentFileApplicationDetail),
    }),
    E.fold(renderAsync, ({applicationProcessingDetails, enrollmentFileApplicationDetail}) => {
      return (
        <ApplicationsPage
          applicationDetails={applicationProcessingDetails}
          enrollmentApplications={enrollmentFileApplicationDetail}
        />
      );
    })
  );
};

export default ESRBPStatusDetails;
